/**
 * Enhanced Email List Form with User Selection
 * 3-Step wizard: Basic Info -> User Selection -> Review & Create
 *
 * Created: January 4, 2026
 */

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { format, addDays } from "date-fns";

import { broadcast_page_name } from "../common-data/DropdowData";
import CmnFilters from "../lead-management/common/CmnFilters";
import CommonFilterStructure from "../common-components/CommonFilterStructure";
import MultiSelect from "../components-react/MultiSelect";
import CloseSubmitBtn from "../common-components/custom-comps/CloseSubmitBtn";
import Loader from "../ui/Loader";
import { fetchApiData } from "../../redux/reducers/commonApiSlice";
import { createEmailList, populateEmailListSubscribers } from "../../redux/email-broadcast/emailBroadcastApi";
import {
  currentMonthCurrentDate,
  currentMonthEndDate,
  currentMonthStartDate,
  currentMonthTomorrowDate,
  currentMonthYesterdayDate,
  IS_LAST_DAY_OF_THE_MONTH,
  nextMonthEndDateFormatted,
} from "../common-data/Converts";
import { ReloadIcon } from "../../images/icons/icons";

// Extended broadcast_page_name with new options
const emailListUserTypes = [
  ...broadcast_page_name,
  {
    value: "active_members",
    label: "Active Members",
    url: "/gym/dashboard/reports/details/v2",
  },
  {
    value: "all_members",
    label: "All Members",
    url: "/gym/dashboard/reports/details/v2",
  },
];

const EmailListFormEnhanced = ({ isEdit, rowData, closeModal, userInfoData, callFunction }) => {
  const dispatch = useDispatch();
  const { data, loading, error } = useSelector((state) => state.common);

  const isAdmin = userInfoData?.account_type === "admin";
  const isGym = userInfoData?.account_type === "gym";
  const isMarketing = userInfoData?.account_type === "marketing";
  const isClusterManager = userInfoData?.account_type === "cluster_manager";

  // Step 1: Basic Info
  const [step, setStep] = useState(1);
  const [basicInfo, setBasicInfo] = useState({
    name: rowData?.name || "",
    description: rowData?.description || "",
    opt_in_type: rowData?.opt_in_type || "single",
    sync_type: rowData?.sync_type || "manual",
  });

  // Step 2: User Selection
  const [selectedUserType, setSelectedUserType] = useState("");
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [totalMembers, setTotalMembers] = useState(0);
  const [allMembers, setAllMembers] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // Filters
  const from = selectedUserType?.value || "";
  const iniAllFilter = {
    selectedGym:
      !isAdmin && !isMarketing && !isClusterManager
        ? {
            label: isGym ? userInfoData?.name : userInfoData?.gym_name || "",
            value:
              userInfoData?.gym_id ||
              userInfoData?.user_id ||
              userInfoData?.uid ||
              "",
          }
        : "",
    selectedGyms: [],
    isActive: "",
    selAddNotAddToLead: "",
    selectedODMStatus: "",
    selectedLMSSorting: "",
    selectedDateAddedSorting: "",
    selectedGender: "",
    selAutomationKeyword: "",
    selLeadStatus: "",
    selUTMSource: "",
    selUTMMedium: "",
    selUTMContent: "",
    selUTMTerm: "",
    selUTMCampaign: "",
    selAssignedLeadDay: "",
    selAssignedLeadHour: "",
    selAssignedLeadMinute: "",
    selAssignLeadFollowupDay: "",
    selAssignLeadFollowupHour: "",
    selAssignLeadFolloupMinute: "",
    leadAddDateFrom: "",
    leadAddDateTo: "",
    leadConvertedDateFrom: "",
    leadConvertedDateTo: "",
    searchString: "",
    selAssignee:
      !isAdmin && !isMarketing && !isClusterManager
        ? {
            label: userInfoData?.name || "",
            value: userInfoData?.uid || userInfoData?.user_id || "",
          }
        : "",
    selectedSubmissionType: "",
    selAddedby: "",
    selAssginee:
      !isAdmin && !isMarketing && !isClusterManager
        ? {
            label: userInfoData?.name || "",
            value: userInfoData?.uid || userInfoData?.user_id || "",
          }
        : "",
    selCustomerIntent: "",
    selFollowupStatus: "",
    selLeadSource: "",
    selectedVendor: "",
    selCard: "",
    setPlan: "",
    selFollowupTime: [],
    selIsRaw: "",
    selLeadFrom: "",
    selFollowupStage: "",
    selOperatorValue: "",
    selConvertedIn: [],
    clickedCard: "",
    selectedWhatsappVerification: "",
    leadFollowupDateFrom: "",
    leadFollowupDateTo: "",
    leadClosureDateFrom: "",
    leadClosureDateTo: "",
    rawLeadAddDateFrom: "",
    rawLeadAddDateTo: "",
    selectedOtherUsers: "",
    selectedStartDate:
      selectedUserType?.value === "renewal_members"
        ? currentMonthTomorrowDate
        : currentMonthStartDate,
    selectedEndDate:
      selectedUserType?.value === "renewal_members"
        ? IS_LAST_DAY_OF_THE_MONTH
          ? nextMonthEndDateFormatted
          : currentMonthEndDate
        : selectedUserType?.value === "unpaid_members"
        ? currentMonthYesterdayDate
        : currentMonthCurrentDate,
    selectedTrxMode: "",
    selectedSubsType: "",
    selectedPlan: "",
    selectedRole: "",
    selRecurringStatus: "",
    selExpiringInDays: "",
    lastCheckedIn: "",
    selLeastCheckinInBetween: [],
    selExpiredDaysAgo: [],
    selButtonEnabled: "",
    selPaymentTimeSlot: [],
    selPaymentSource: "",
    selectedPriceRangeFrom: "",
    selectedPriceRangeTo: "",
  };
  const [allFilters, setAllFilters] = useState(iniAllFilter);

  const iniFilters = {
    page: 1,
    limit: 5000,
    auto_gym_map: from === "wati" ? 1 : "",
    training_gym_data: from === "odm" ? 0 : "",
    filter_type:
      from === "unpaid_members" || selectedUserType?.value === "unpaid_members"
        ? "unpaid"
        : from === "active_members"
        ? "active"
        : from === "expiring_today"
        ? "expire_today"
        : from === "expiring_this_month" ||
          selectedUserType?.value === "renewal_members"
        ? "expire_month"
        : from === "all_members"
        ? "all"
        : "",
    gym_id: allFilters.selectedGym?.value || "",
    added_to_lead: allFilters.selAddNotAddToLead?.value || "",
    plan_status: allFilters.isActive?.value || "",
    is_payment_done: allFilters.selectedODMStatus?.value || "",
    not_added_lms_sort: allFilters.selectedLMSSorting || "",
    date_added_sort: allFilters.selectedDateAddedSorting || "",
    gender: allFilters.selectedGender?.value || "",
    keyword_name: allFilters.selAutomationKeyword?.label || "",
    lead_status: allFilters.selLeadStatus?.value || "",
    utm_source: allFilters.selUTMSource?.value || "",
    utm_medium: allFilters.selUTMMedium?.value || "",
    utm_content: allFilters.selUTMContent?.value || "",
    utm_term: allFilters.selUTMTerm?.value || "",
    utm_campaign: allFilters.selUTMCampaign?.value || "",
    start_date:
      selectedUserType?.value === "renewal_members" ||
      selectedUserType?.value === "unpaid_members"
        ? allFilters.selectedStartDate || ""
        : allFilters.leadAddDateFrom || "",
    end_date:
      selectedUserType?.value === "renewal_members" ||
      selectedUserType?.value === "unpaid_members"
        ? allFilters.selectedEndDate || ""
        : allFilters.leadAddDateTo || "",
    convert_start_date: allFilters.leadConvertedDateFrom || "",
    convert_end_date: allFilters.leadConvertedDateTo || "",
    date_from: allFilters.leadAddDateFrom || "",
    date_to: allFilters.leadAddDateTo || "",
    submission_type: allFilters.selectedSubmissionType?.value || "",
    assisted_by_id: allFilters.selAssignee?.value || "",
    assigned_to: allFilters.selAssginee?.value || "",
    added_by: allFilters.selAddedby?.value || "",
    follow_up_status: allFilters.selFollowupStatus?.value || "",
    source_name: allFilters.selLeadSource?.value || "",
    btl_vendor_id: allFilters.selectedVendor?.value || "",
    lead_type: allFilters.selCard?.value || "",
    intent_id: allFilters.selCustomerIntent?.value || "",
    plan_type: allFilters.setPlan?.value || "",
    is_raw_lead: allFilters.selIsRaw?.value || "",
    wap_image: allFilters.selectedWhatsappVerification?.value || "",
    follow_up_date_from: allFilters.leadFollowupDateFrom || "",
    follow_up_date_to: allFilters.leadFollowupDateTo || "",
    expected_closure_date_from: allFilters.leadClosureDateFrom || "",
    expected_closure_date_to: allFilters.leadClosureDateTo || "",
    rawlead_start_date: allFilters.rawLeadAddDateFrom || "",
    rawlead_end_date: allFilters.rawLeadAddDateTo || "",
    operator_value: allFilters.selOperatorValue?.value || "",
    sub_added_by: allFilters?.selectedOtherUsers?.value || "",
    trx_id: allFilters.selectedTrxMode?.value || "",
    membership_type: allFilters.selectedSubsType.value || "",
    plan_duration: allFilters.selectedPlan?.value || "",
    addon_cat_id:
      allFilters.selectedSubsType?.value === "addon" &&
      allFilters.selectedPlan?.value
        ? allFilters.selectedPlan.value
        : "",
    account_type: allFilters.selectedRole?.value || "",
    is_recurring: allFilters.selRecurringStatus?.value || "",
    last_checkin_days_ago: allFilters.lastCheckedIn?.value || "",
    expiring_indays: allFilters.selExpiringInDays?.value || "",
    expired_days_ago: "",
    is_btn_enabled: allFilters.selButtonEnabled?.value || "",
    payment_time_slot: "",
    payment_source: allFilters.selPaymentSource?.value || "",
    price_range_from: allFilters.selectedPriceRangeFrom?.value || "",
    price_range_to: allFilters.selectedPriceRangeTo?.value || "",
  };
  const [filters, setFilters] = useState(iniFilters);

  const [openDateRanger, setOpenDateRanger] = useState(false);
  const initialRangeState = [
    {
      startDate: new Date(),
      endDate: addDays(new Date(), 7),
      key: "selection",
    },
  ];
  const [rangeState, setRangeState] = useState(initialRangeState);
  const [submitting, setSubmitting] = useState(false);

  const onResetFilters = () => {
    setAllFilters(iniAllFilter);
  };

  const handleDateRangeChange = (item) => {
    setRangeState([item?.selection]);
    const formattedStartDate = format(item?.selection?.startDate, "yyyy-MM-dd");
    const formattedEndDate = format(item?.selection?.endDate, "yyyy-MM-dd");
    setAllFilters({
      ...allFilters,
      selectedStartDate: formattedStartDate,
      selectedEndDate: formattedEndDate,
    });
  };

  const clearDateRanger = () => {
    setOpenDateRanger(false);
    setRangeState(initialRangeState);
    setAllFilters({
      ...allFilters,
      selectedStartDate: "",
      selectedEndDate: "",
    });
  };

  const getLabelFrom = (value) => {
    const option = emailListUserTypes.find((opt) => opt.value === value);
    return option?.label || value;
  };

  // Fetch members based on filters
  const getMembers = () => {
    const endpoint = selectedUserType?.url;
    if (endpoint) {
      dispatch(
        fetchApiData({
          method: "GET",
          endpoint: endpoint,
          payload: {
            filters: filters,
          },
          token: userInfoData?.token,
        })
      );
    }
  };

  useEffect(() => {
    if (selectedUserType) {
      getMembers();
      setSelectedMembers([]);
      setTotalMembers(0);
      setSelectAll(false);
    }
  }, [filters]);

  useEffect(() => {
    setAllFilters((prev) => {
      if (!prev.selectedGym) return prev;
      const filtered = prev.selectedGyms.filter(
        (gym) => gym?.value !== prev.selectedGym?.value
      );
      return {
        ...prev,
        selectedGyms: [prev.selectedGym, ...filtered],
      };
    });
  }, [allFilters.selectedGym]);

  useEffect(() => {
    const extractCheckinTimes = (checkinArray) => {
      let last_checkin_start_end_time = [];
      checkinArray?.length > 0 &&
        checkinArray.forEach((item) => {
          const itemValue = item.value;
          last_checkin_start_end_time.push(itemValue);
        });
      return {
        last_checkin_start_end_time: last_checkin_start_end_time.join(", "),
      };
    };

    const extractExpiredDaysAgo = (arr) => {
      let days_ago = [];
      arr?.length > 0 &&
        arr.forEach((item) => {
          const itemValue = item.value;
          days_ago.push(itemValue);
        });
      return {
        days_ago: days_ago.join(", "),
      };
    };

    const extractPaymentTimes = (arr) => {
      let payment_times = [];
      arr?.length > 0 &&
        arr.forEach((item) => {
          const itemValue = item.value;
          payment_times.push(itemValue);
        });
      return {
        payment_times: payment_times.join(", "),
      };
    };

    const extractGyms = (arr) => {
      let gyms = [];
      arr?.length > 0 &&
        arr.forEach((item) => {
          const itemValue = item.value;
          gyms.push(itemValue);
        });
      return {
        gyms: gyms.join(", "),
      };
    };

    const { last_checkin_start_end_time } = extractCheckinTimes(
      allFilters.selLeastCheckinInBetween
    );
    const { days_ago } = extractExpiredDaysAgo(allFilters.selExpiredDaysAgo);
    const { payment_times } = extractPaymentTimes(
      allFilters.selPaymentTimeSlot
    );
    const { gyms } =
      allFilters.selectedGyms?.length > 0
        ? extractGyms(allFilters.selectedGyms)
        : allFilters.selectedGym?.value || "";

    setFilters((prev) => ({
      ...prev,
      auto_gym_map: from === "wati" ? 1 : "",
      training_gym_data: from === "odm" ? 0 : "",
      filter_type:
        from === "unpaid_members" ||
        selectedUserType?.value === "unpaid_members"
          ? "unpaid"
          : from === "active_members"
          ? "active"
          : from === "expiring_today"
          ? "expire_today"
          : from === "expiring_this_month" ||
            selectedUserType?.value === "renewal_members"
          ? "expire_month"
          : from === "all_members"
          ? "all"
          : "",
      gym_id: gyms || allFilters.selectedGym?.value || "",
      added_to_lead: allFilters.selAddNotAddToLead?.value || "",
      plan_status: allFilters.isActive?.value || "",
      is_payment_done: allFilters.selectedODMStatus?.value || "",
      not_added_lms_sort: allFilters.selectedLMSSorting || "",
      date_added_sort: allFilters.selectedDateAddedSorting || "",
      gender: allFilters.selectedGender?.value || "",
      keyword_name: allFilters.selAutomationKeyword?.label || "",
      lead_status: allFilters.selLeadStatus?.value || "",
      utm_source: allFilters.selUTMSource?.value || "",
      utm_medium: allFilters.selUTMMedium?.value || "",
      utm_content: allFilters.selUTMContent?.value || "",
      utm_term: allFilters.selUTMTerm?.value || "",
      utm_campaign: allFilters.selUTMCampaign?.value || "",
      start_date:
        selectedUserType?.value === "renewal_members" ||
        selectedUserType?.value === "unpaid_members"
          ? allFilters.selectedStartDate || ""
          : allFilters.leadAddDateFrom || "",
      end_date:
        selectedUserType?.value === "renewal_members" ||
        selectedUserType?.value === "unpaid_members"
          ? allFilters.selectedEndDate || ""
          : allFilters.leadAddDateTo || "",
      convert_start_date: allFilters.leadConvertedDateFrom || "",
      convert_end_date: allFilters.leadConvertedDateTo || "",
      date_from: allFilters.leadAddDateFrom || "",
      date_to: allFilters.leadAddDateTo || "",
      submission_type: allFilters.selectedSubmissionType?.value || "",
      assisted_by_id: allFilters.selAssignee?.value || "",
      assigned_to: allFilters.selAssginee?.value || "",
      added_by: allFilters.selAddedby?.value || "",
      follow_up_status: allFilters.selFollowupStatus?.value || "",
      source_name: allFilters.selLeadSource?.value || "",
      btl_vendor_id: allFilters.selectedVendor?.value || "",
      lead_type: allFilters.selCard?.value || "",
      intent_id: allFilters.selCustomerIntent?.value || "",
      plan_type: allFilters.setPlan?.value || "",
      is_raw_lead: allFilters.selIsRaw?.value || "",
      wap_image: allFilters.selectedWhatsappVerification?.value || "",
      follow_up_date_from: allFilters.leadFollowupDateFrom || "",
      follow_up_date_to: allFilters.leadFollowupDateTo || "",
      expected_closure_date_from: allFilters.leadClosureDateFrom || "",
      expected_closure_date_to: allFilters.leadClosureDateTo || "",
      rawlead_start_date: allFilters.rawLeadAddDateFrom || "",
      rawlead_end_date: allFilters.rawLeadAddDateTo || "",
      operator_value: allFilters.selOperatorValue?.value || "",
      sub_added_by: allFilters?.selectedOtherUsers?.value || "",
      trx_id: allFilters.selectedTrxMode?.value || "",
      membership_type: allFilters.selectedSubsType.value || "",
      plan_duration: allFilters.selectedPlan?.value
        ? allFilters.selectedPlan.value
        : "",
      addon_cat_id:
        allFilters.selectedSubsType?.value === "addon" &&
        allFilters.selectedPlan?.value
          ? allFilters.selectedPlan.value
          : "",
      account_type: allFilters.selectedRole?.value || "",
      is_recurring: allFilters.selRecurringStatus?.value || "",
      last_checkin_days_ago: allFilters.lastCheckedIn?.value || "",
      last_checkin_time_filter: last_checkin_start_end_time || "",
      expiring_indays: allFilters.selExpiringInDays?.value || "",
      expired_days_ago: days_ago || "",
      is_btn_enabled: allFilters.selButtonEnabled?.value || "",
      payment_time_slot: payment_times || "",
      payment_source: allFilters.selPaymentSource?.value || "",
      price_range_from: allFilters.selectedPriceRangeFrom?.value || "",
      price_range_to: allFilters.selectedPriceRangeTo?.value || "",
    }));
  }, [from, allFilters, selectedUserType]);

  useEffect(() => {
    if (from && !loading && !error && data?.status && data?.data) {
      const dropdownOptions = data.data?.map((item) => ({
        label: `${item.name || item.user_name || "NA"}/${
          item.mobile || item.user_mobile || item.email || ""
        }`,
        value:
          from === "wati" || from === "recurring" || from === "odm"
            ? item.uid
            : from === "lms"
            ? item.lead_id
            : item.user_id,
        email: item.email || item.user_email || "",
        mobile: item.mobile || item.user_mobile || "",
        info: item,
      }));

      setAllMembers(dropdownOptions);
      setTotalMembers(data.data?.length);
    } else {
      setAllMembers([]);
      setSelectedMembers([]);
      setTotalMembers(0);
    }
  }, [data, loading, error, from]);

  // Handle Select All
  useEffect(() => {
    if (selectAll && allMembers.length > 0) {
      setSelectedMembers(allMembers);
    } else if (!selectAll && selectedMembers.length === allMembers.length) {
      setSelectedMembers([]);
    }
  }, [selectAll]);

  // Step validation
  const validateStep1 = () => {
    if (!basicInfo.name.trim()) {
      toast.error("List name is required");
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!selectedUserType) {
      toast.error("Please select a user type");
      return false;
    }
    if (selectedMembers.length === 0) {
      toast.error("Please select at least one member");
      return false;
    }
    return true;
  };

  // Navigation
  const handleNext = () => {
    if (step === 1 && validateStep1()) {
      setStep(2);
    } else if (step === 2 && validateStep2()) {
      setStep(3);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  // Submit
  const handleSubmit = async () => {
    if (!validateStep1() || !validateStep2()) {
      return;
    }

    setSubmitting(true);
    try {
      // Step 1: Create the list
      const listResponse = await createEmailList(basicInfo, userInfoData?.token);

      if (!listResponse?.status) {
        toast.error(listResponse?.message || "Failed to create list");
        setSubmitting(false);
        return;
      }

      const listId = listResponse.data?.uid || listResponse.data?.id;

      // Step 2: Populate subscribers
      const subscriberIds = selectedMembers.map((m) => ({
        user_id: m.value,
        email: m.email,
        mobile: m.mobile,
      }));

      const populateResponse = await populateEmailListSubscribers(
        listId,
        {
          user_type: selectedUserType.value,
          filters: filters,
          subscribers: subscriberIds,
          select_all: selectAll,
        },
        userInfoData?.token
      );

      if (populateResponse?.status) {
        toast.success(`List created with ${selectedMembers.length} subscriber(s)`);
        callFunction();
        closeModal();
      } else {
        toast.warning("List created but failed to add some subscribers");
        callFunction();
        closeModal();
      }
    } catch (error) {
      toast.error(error.message || "Failed to create list");
    } finally {
      setSubmitting(false);
    }
  };

  // Render Steps
  const renderStep1 = () => (
    <div className="p-3">
      <h5 className="mb-3">Step 1: Basic Information</h5>

      <div className="mb-3">
        <label className="form-label">
          List Name <span className="required">*</span>
        </label>
        <input
          type="text"
          className="form-control"
          value={basicInfo.name}
          onChange={(e) => setBasicInfo({ ...basicInfo, name: e.target.value })}
          placeholder="e.g., Active Members January 2026"
          required
        />
      </div>

      <div className="mb-3">
        <label className="form-label">Description</label>
        <textarea
          className="form-control"
          value={basicInfo.description}
          onChange={(e) => setBasicInfo({ ...basicInfo, description: e.target.value })}
          placeholder="Brief description of this list"
          rows={3}
        />
      </div>

      <div className="mb-3">
        <label className="form-label">Opt-in Type</label>
        <select
          className="form-select"
          value={basicInfo.opt_in_type}
          onChange={(e) => setBasicInfo({ ...basicInfo, opt_in_type: e.target.value })}
        >
          <option value="single">Single Opt-in (Immediate)</option>
          <option value="double">Double Opt-in (Confirmation Required)</option>
        </select>
        <small className="text-muted">
          Single opt-in adds subscribers immediately. Double opt-in requires email confirmation.
        </small>
      </div>

      <div className="mb-4">
        <label className="form-label">Sync Type</label>
        <select
          className="form-select"
          value={basicInfo.sync_type}
          onChange={(e) => setBasicInfo({ ...basicInfo, sync_type: e.target.value })}
        >
          <option value="manual">Manual (One-time population)</option>
          <option value="auto_sync">Auto-Sync (Updates automatically)</option>
        </select>
        <small className="text-muted">
          Manual lists are static. Auto-sync lists update automatically based on filters.
        </small>
      </div>

      <div className="d-flex gap-2">
        <button type="button" className="btn btn-primary flex-grow-1" onClick={handleNext}>
          Next: Select Users
        </button>
        <button type="button" className="btn btn-secondary" onClick={closeModal}>
          Cancel
        </button>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="form-container boradcast-log-form">
      <div className="formContainer">
        <h5 className="mb-3">Step 2: Select Users</h5>

        <div className="formInputsGrid threeGridSystem mt15">
          <div className="formInputBox page-name-card span3">
            <label htmlFor="selectedUserType" className="formInputLabel">
              Select Audience Type{" "}
              <small className="gray">(Choose user type for this list)</small>{" "}
              <span className="required">*</span>
            </label>

            <div className="checkbox-group two-column-checkboxes">
              {emailListUserTypes.map((option) => (
                <label
                  key={option.value}
                  className="checkbox-label page-card-checkbox-lable"
                >
                  <input
                    type="checkbox"
                    checked={selectedUserType?.value === option.value}
                    onChange={() =>
                      setSelectedUserType(
                        selectedUserType?.value === option.value ? "" : option
                      )
                    }
                  />
                  <span className="custom-checkbox"></span>
                  {option.label}
                </label>
              ))}
            </div>
          </div>
        </div>

        {selectedUserType?.value && (
          <>
            {selectedUserType?.value === "unpaid_members" ||
            selectedUserType?.value === "renewal_members" ? (
              <CommonFilterStructure
                from={"sales-table"}
                isSegment={true}
                decryptedText={userInfoData}
                setFilters={setAllFilters}
                filters={allFilters}
                openDateRanger={openDateRanger}
                onClickReset={onResetFilters}
                handleDateRangeChange={handleDateRangeChange}
                setOpenDateRanger={setOpenDateRanger}
                rangeState={rangeState}
                clearDateRanger={clearDateRanger}
                clickedCard={
                  selectedUserType?.value === "unpaid_members"
                    ? "unpaid_members"
                    : selectedUserType?.value === "renewal_members"
                    ? "expiring_this_month"
                    : ""
                }
                otherUserLabel={
                  selectedUserType?.value === "unpaid_members" ||
                  selectedUserType?.value === "renewal_members"
                    ? "Added By"
                    : null
                }
              />
            ) : (
              <CmnFilters
                key={selectedUserType?.value}
                from={
                  selectedUserType?.value === "odm" ||
                  selectedUserType?.value === "wati"
                    ? "one-day-membership"
                    : selectedUserType?.value === "lms"
                    ? "lead-management"
                    : selectedUserType?.value === "walkins"
                    ? "walkins-leads"
                    : selectedUserType?.value === "recurring"
                    ? "recurring-999"
                    : ""
                }
                selectedTab={
                  selectedUserType?.value === "odm"
                    ? 1
                    : selectedUserType?.value === "wati"
                    ? 2
                    : ""
                }
                isSegment={true}
                isBroadcastLogs={true}
                userInfoData={userInfoData}
                onClickReset={onResetFilters}
                filters={allFilters}
                setFilters={setAllFilters}
                clickedCard={
                  selectedUserType?.value === "wati" ? "wati_leads" : null
                }
                isReload={getMembers}
              />
            )}
          </>
        )}

        <div className="formInputsGrid oneGridSystem">
          <div className="formInputBox page-name-card">
            <div className="flexBetween">
              <label htmlFor="selectedMembers" className="formInputLabel">
                Choose {getLabelFrom(selectedUserType?.value)} members{" "}
                <small className="gray uppercase">
                  (Selected: {selectedMembers?.length} / Total: {totalMembers})
                </small>
                <span className="required">*</span>
                <span
                  className="broadcast-reload-icon"
                  onClick={getMembers}
                  title="Reload members data"
                >
                  <ReloadIcon />
                </span>
              </label>

              <label className="checkbox-label ms-3">
                <input
                  type="checkbox"
                  checked={selectAll}
                  onChange={(e) => setSelectAll(e.target.checked)}
                />
                <span className="custom-checkbox"></span>
                Select All
              </label>
            </div>

            {loading ? (
              <Loader />
            ) : (
              <div className="commonWrapper no-border multi-select multi-show-option">
                <MultiSelect
                  options={allMembers}
                  state={selectedMembers}
                  setState={setSelectedMembers}
                  name="select multiple members from the list"
                  preventKeyboardOpen={false}
                  isShowList={true}
                />
              </div>
            )}

            <span className="tiny-info-label">
              ⓘ Up to 5000 members can be fetched at a time!
            </span>
          </div>
        </div>

        <div className="d-flex gap-2 mt-3">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleBack}
          >
            Back
          </button>
          <button
            type="button"
            className="btn btn-primary flex-grow-1"
            onClick={handleNext}
            disabled={loading}
          >
            Next: Review & Create
          </button>
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="p-3">
      <h5 className="mb-3">Step 3: Review & Confirm</h5>

      <div className="card mb-3">
        <div className="card-body">
          <h6 className="card-subtitle mb-2 text-muted">List Information</h6>
          <table className="table table-sm">
            <tbody>
              <tr>
                <td className="fw-medium">Name:</td>
                <td>{basicInfo.name}</td>
              </tr>
              {basicInfo.description && (
                <tr>
                  <td className="fw-medium">Description:</td>
                  <td>{basicInfo.description}</td>
                </tr>
              )}
              <tr>
                <td className="fw-medium">Opt-in Type:</td>
                <td className="text-capitalize">{basicInfo.opt_in_type}</td>
              </tr>
              <tr>
                <td className="fw-medium">Sync Type:</td>
                <td className="text-capitalize">
                  {basicInfo.sync_type === "auto_sync" ? "Auto-Sync" : "Manual"}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-body">
          <h6 className="card-subtitle mb-2 text-muted">User Selection</h6>
          <table className="table table-sm">
            <tbody>
              <tr>
                <td className="fw-medium">User Type:</td>
                <td>{getLabelFrom(selectedUserType?.value)}</td>
              </tr>
              <tr>
                <td className="fw-medium">Total Subscribers:</td>
                <td className="fw-bold text-primary">{selectedMembers.length}</td>
              </tr>
              {allFilters.selectedGym && (
                <tr>
                  <td className="fw-medium">Gym Filter:</td>
                  <td>{allFilters.selectedGym.label}</td>
                </tr>
              )}
              {allFilters.selectedGender && (
                <tr>
                  <td className="fw-medium">Gender Filter:</td>
                  <td>{allFilters.selectedGender.label}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="alert alert-info">
        <small>
          <strong>Note:</strong> This will create the list and add{" "}
          {selectedMembers.length} subscriber(s). You can add more subscribers later.
        </small>
      </div>

      <div className="d-flex gap-2">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={handleBack}
        >
          Back
        </button>
        <button
          type="button"
          className="btn btn-primary flex-grow-1"
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? "Creating List..." : "Create List & Add Subscribers"}
        </button>
      </div>
    </div>
  );

  return (
    <div className="email-list-form-enhanced">
      {/* Step Indicator */}
      <div className="step-indicator mb-4 px-3 pt-3">
        <div className="d-flex justify-content-between align-items-center">
          <div className={`step ${step >= 1 ? "active" : ""}`}>
            <div className="step-number">1</div>
            <div className="step-label">Basic Info</div>
          </div>
          <div className="step-divider"></div>
          <div className={`step ${step >= 2 ? "active" : ""}`}>
            <div className="step-number">2</div>
            <div className="step-label">Select Users</div>
          </div>
          <div className="step-divider"></div>
          <div className={`step ${step >= 3 ? "active" : ""}`}>
            <div className="step-number">3</div>
            <div className="step-label">Review</div>
          </div>
        </div>
      </div>

      {/* Step Content */}
      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}
      {step === 3 && renderStep3()}

      <style jsx>{`
        .step-indicator {
          max-width: 600px;
          margin: 0 auto;
        }
        .step {
          text-align: center;
          flex: 1;
        }
        .step-number {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: #e9ecef;
          color: #6c757d;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          margin-bottom: 8px;
        }
        .step.active .step-number {
          background: #0d6efd;
          color: white;
        }
        .step-label {
          font-size: 12px;
          color: #6c757d;
        }
        .step.active .step-label {
          color: #0d6efd;
          font-weight: 500;
        }
        .step-divider {
          flex: 1;
          height: 2px;
          background: #e9ecef;
          margin: 0 16px;
          align-self: center;
          margin-top: -20px;
        }
      `}</style>
    </div>
  );
};

export default EmailListFormEnhanced;
